import type {
  AuthConnectionIssue,
  AuthConnectionIssueReason,
  AuthConnectionOperation,
} from "./types"

export const AUTH_CONNECTION_BANNER_DEBOUNCE_MS = 1_500

const AUTH_CONNECTION_OPERATION = Symbol("openanalyst.auth.connectionOperation")
const URL_PATTERN = /\bhttps?:\/\/[^\s)]+/gi
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g
const TOKENISH_PATTERN = /\b(?:access|refresh|id|session|auth)[_-]?token[:=]\s*[^\s,;]+/gi
const MAX_ERROR_MESSAGE_LENGTH = 180

export function tagAuthConnectionOperation<T>(error: T, operation: AuthConnectionOperation): T {
  if ((typeof error !== "object" && typeof error !== "function") || error === null) return error
  try {
    Object.defineProperty(error, AUTH_CONNECTION_OPERATION, {
      configurable: true,
      value: operation,
    })
  } catch {}
  return error
}

export function authConnectionOperation(error: unknown, depth = 0): AuthConnectionOperation | null {
  if (!error || depth > 5) return null
  if (typeof error === "object" || typeof error === "function") {
    const tagged = (error as { [AUTH_CONNECTION_OPERATION]?: unknown })[AUTH_CONNECTION_OPERATION]
    if (isAuthConnectionOperation(tagged)) return tagged
  }
  if (error instanceof Error && error.cause) return authConnectionOperation(error.cause, depth + 1)
  return null
}

export function authConnectionReasonForOperation(
  operation: AuthConnectionOperation,
  error: unknown,
): AuthConnectionIssueReason {
  const message = errorMessage(error).toLowerCase()
  if (message.includes("desktop token refresh timed out")) return "desktop_auth_broker_timeout"
  if (message.includes("could not reach openanalyst to confirm your session")) {
    return operation === "desktop_auth_restore" ? "desktop_auth_restore_unreachable" : "openanalyst_account_service_unreachable"
  }
  if (operation === "navigator_offline") return "device_offline"
  if (operation === "desktop_auth_restore") return "desktop_auth_restore_unreachable"
  if (operation === "session_check") return "session_check_unreachable"
  if (operation === "profile_fetch") return "profile_fetch_unreachable"
  if (operation === "openanalyst_account") return "openanalyst_account_service_unreachable"
  return "auth_refresh_unreachable"
}

export function createAuthConnectionIssue(input: {
  reason: AuthConnectionIssueReason
  operation: AuthConnectionOperation
  error?: unknown
  retryDelayMs?: number | null
  now: number
  previous?: AuthConnectionIssue | null
}): AuthConnectionIssue {
  const previous =
    input.previous?.reason === input.reason && input.previous.operation === input.operation
      ? input.previous
      : null
  const sanitized = sanitizeAuthConnectionError(input.error)
  return {
    reason: input.reason,
    operation: input.operation,
    retryDelayMs: input.retryDelayMs ?? null,
    firstDetectedAt: previous?.firstDetectedAt ?? input.now,
    lastDetectedAt: input.now,
    ...(sanitized.errorName ? { errorName: sanitized.errorName } : {}),
    ...(sanitized.errorCode ? { errorCode: sanitized.errorCode } : {}),
    ...(sanitized.errorMessage ? { errorMessage: sanitized.errorMessage } : {}),
  }
}

export function authConnectionBannerText(issue: AuthConnectionIssue | null) {
  switch (issue?.reason) {
    case "device_offline":
      return {
        title: "Connection lost",
        message: "Reconnecting... You're still signed in and your work is safe.",
      }
    case "desktop_auth_restore_unreachable":
      return {
        title: "Reconnecting account session",
        message: "OpenAnalyst could not restore the desktop session yet. Your sign-in is safe.",
      }
    case "auth_refresh_unreachable":
      return {
        title: "Reconnecting to account services",
        message: "OpenAnalyst account refresh is delayed. Your work is safe.",
      }
    case "session_check_unreachable":
      return {
        title: "Account check delayed",
        message: "OpenAnalyst could not verify the current session yet. Retrying in the background.",
      }
    case "profile_fetch_unreachable":
      return {
        title: "Profile check delayed",
        message: "OpenAnalyst could not refresh profile details yet. Retrying in the background.",
      }
    case "desktop_auth_broker_timeout":
      return {
        title: "Desktop auth broker delayed",
        message: "The desktop auth broker timed out while reconnecting. Retrying in the background.",
      }
    case "openanalyst_account_service_unreachable":
    default:
      return {
        title: "OpenAnalyst account service unavailable",
        message: "OpenAnalyst account services are temporarily unreachable. Retrying in the background.",
      }
  }
}

export function authConnectionLoadingMessage(issue: AuthConnectionIssue | null) {
  const text = authConnectionBannerText(issue)
  return `${text.title} - ${text.message}`
}

export function authConnectionDebugLabel(issue: AuthConnectionIssue | null) {
  if (!issue) return ""
  const parts = [
    `reason=${issue.reason}`,
    `operation=${issue.operation}`,
    `retryDelayMs=${issue.retryDelayMs ?? "none"}`,
    `firstDetectedAt=${new Date(issue.firstDetectedAt).toISOString()}`,
    `lastDetectedAt=${new Date(issue.lastDetectedAt).toISOString()}`,
  ]
  if (issue.errorName) parts.push(`errorName=${issue.errorName}`)
  if (issue.errorCode) parts.push(`errorCode=${issue.errorCode}`)
  if (issue.errorMessage) parts.push(`errorMessage=${issue.errorMessage}`)
  return parts.join("; ")
}

export function shouldDebounceAuthConnectionIssue(issue: AuthConnectionIssue | null) {
  return !!issue && issue.reason !== "device_offline"
}

export function sanitizeAuthConnectionError(error: unknown) {
  const name = error instanceof Error ? error.name : typeof error === "string" ? "Error" : undefined
  const code = errorCode(error)
  const rawMessage = errorMessage(error)
  const message = rawMessage ? scrubSensitiveText(rawMessage).slice(0, MAX_ERROR_MESSAGE_LENGTH) : undefined
  return {
    ...(name ? { errorName: scrubSensitiveText(name).slice(0, 80) } : {}),
    ...(code ? { errorCode: scrubSensitiveText(code).slice(0, 80) } : {}),
    ...(message ? { errorMessage: message } : {}),
  }
}

function isAuthConnectionOperation(value: unknown): value is AuthConnectionOperation {
  return (
    value === "navigator_offline" ||
    value === "desktop_auth_restore" ||
    value === "auth_refresh" ||
    value === "session_check" ||
    value === "profile_fetch" ||
    value === "openanalyst_account"
  )
}

function errorCode(error: unknown, depth = 0): string | undefined {
  if (!error || depth > 5) return undefined
  if (typeof error === "object" && error !== null) {
    const code = (error as { code?: unknown }).code
    if (typeof code === "string" && code.trim()) return code.trim()
  }
  if (error instanceof Error && error.cause) return errorCode(error.cause, depth + 1)
}

function errorMessage(error: unknown, depth = 0): string {
  if (!error || depth > 5) return ""
  if (typeof error === "string") return error
  if (error instanceof Error) return error.message || errorMessage(error.cause, depth + 1)
  return ""
}

function scrubSensitiveText(value: string) {
  return value
    .replace(URL_PATTERN, "[url]")
    .replace(BEARER_PATTERN, "Bearer [redacted]")
    .replace(JWT_PATTERN, "[jwt]")
    .replace(TOKENISH_PATTERN, (match) => match.split(/[:=]/)[0] + "=[redacted]")
    .trim()
}
